import { api } from '@/lib/api';
import { resolveUploadsUrl } from '@/lib/resolve-uploads-url';

/** Réponse POST `/video/generate-subtitles` (transcription + WebVTT). */
export interface VideoAutoCaptionsResult {
  trackUrl: string;
  language: string;
  segments: Array<{ text: string; start: number; end: number }>;
  fullText: string;
  format: 'vtt';
}

export interface UserVideoCategory {
  id: string;
  name: string;
  color?: string | null;
}

export interface UserVideoAsset {
  id: string;
  title: string | null;
  description?: string | null;
  status?: 'draft' | 'published';
  filename: string;
  url: string;
  thumbnailUrl?: string | null;
  mimeType?: string | null;
  fileSize?: number | null;
  duration?: number | null;
  resolution?: string | null;
  tags?: string[];
  thumbnails?: string[];
  categories?: UserVideoCategory[];
  studioAudioTrack?: 'human' | 'ai';
  studioPreferredTtsVoiceId?: string | null;
  trimStart?: number | null;
  trimEnd?: number | null;
  cropX?: number | null;
  cropY?: number | null;
  cropW?: number | null;
  cropH?: number | null;
  segments?: any[] | null;
  audioClips?: any[] | null;
  camSegments?: any[] | null;
  /** Fichier webcam séparé (PiP serveur au publish). */
  camVideoUrl?: string | null;
  captionsUrl?: string | null;
  studioBgImageUrl?: string | null;
  studioVideoLayoutScale?: number | null;
  studioVideoLayoutPosX?: number | null;
  studioVideoLayoutPosY?: number | null;
  /** Ordre des calques composite : premier = arrière-plan */
  studioLayerOrder?: string[] | null;
  /** Calques image / texte (composite client) */
  studioOverlays?: unknown[] | null;
  studioMicNoiseReduction?: boolean;
  /** Gain linéaire micro (1 = 100 %). */
  studioMicGain?: number;
  /** Piste micro coupée au publish (ffmpeg). */
  muted?: boolean;
  createdAt?: string;
}

export type UserVideoUpdatePayload = {
  title?: string;
  description?: string | null;
  categoryIds?: string[];
  status?: 'draft' | 'published';
  studioAudioTrack?: 'human' | 'ai';
  studioPreferredTtsVoiceId?: string | null;
  thumbnailUrl?: string;
  trimStart?: number | null;
  trimEnd?: number | null;
  cropX?: number | null;
  cropY?: number | null;
  cropW?: number | null;
  cropH?: number | null;
  segments?: any[] | null;
  audioClips?: any[] | null;
  camSegments?: any[] | null;
  camVideoUrl?: string | null;
  captionsUrl?: string | null;
  muted?: boolean;
  studioBgImageUrl?: string | null;
  studioVideoLayoutScale?: number | null;
  studioVideoLayoutPosX?: number | null;
  studioVideoLayoutPosY?: number | null;
  studioLayerOrder?: string[] | null;
  studioOverlays?: unknown[] | null;
  studioMicNoiseReduction?: boolean;
  studioMicGain?: number;
  tags?: string[];
};

interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface PaginatedEnvelope<T> extends ApiEnvelope<T[]> {
  pagination: PaginationMeta;
}

export const videoApi = {
  list: (params?: { page?: number; limit?: number }) =>
    api.get<PaginatedEnvelope<UserVideoAsset>>('/video', params),

  get: (id: string) => api.get<ApiEnvelope<UserVideoAsset>>(`/video/${id}`),

  upload: (file: File, onProgress?: (percent: number) => void) => {
    const formData = new FormData();
    formData.append('video', file);
    return api.upload('/video/upload', formData, onProgress) as Promise<{ success: boolean; data: UserVideoAsset }>;
  },

  /** Upload du fichier webcam séparé pour une vidéo studio existante (PiP différé au publish). */
  uploadCam: (videoId: string, file: File, onProgress?: (percent: number) => void) => {
    const formData = new FormData();
    formData.append('cam', file);
    return api.upload(`/video/${videoId}/upload-cam`, formData, onProgress) as Promise<{
      success: boolean;
      data: { camVideoUrl: string };
    }>;
  },

  /** Upload d'une miniature personnalisée pour la vidéo. */
  uploadThumbnail: (videoId: string, file: File, onProgress?: (percent: number) => void) => {
    const formData = new FormData();
    formData.append('thumbnail', file);
    return api.upload(`/video/${videoId}/thumbnail`, formData, onProgress) as Promise<{
      success: boolean;
      data: { thumbnailUrl: string };
    }>;
  },

  update: (id: string, data: UserVideoUpdatePayload) =>
    api.put<ApiEnvelope<UserVideoAsset>>(`/video/${id}`, data),

  /** Apply trim + crop via ffmpeg and mark as published. */
  publish: (id: string) =>
    api.post<ApiEnvelope<UserVideoAsset>>(`/video/${id}/publish`, {}, { timeout: 600_000 }),

  delete: (id: string) => api.delete<void>(`/video/${id}`),

  resolveUrl: (url: string) => resolveUploadsUrl(url) ?? '',

  /** Extraction audio (ffmpeg) + transcription Mistral → fichier WebVTT sous `/uploads/subtitles/`. */
  generateAutoCaptions: (videoUrl: string, language?: string) =>
    api.post<{ success: boolean; data: VideoAutoCaptionsResult }>(
      '/video/generate-subtitles',
      { videoUrl, language },
      { timeout: 600_000 },
    ),

  /** Designer : enregistre un WebVTT édité (nouveau fichier) ; optionnellement synchronise la bibliothèque vidéo. */
  saveSubtitlesFromEditor: (body: { vtt: string; userVideoId?: string }) =>
    api.post<{ success: boolean; data: { trackUrl: string } }>('/video/save-subtitles', body, { timeout: 120_000 }),

  /** Traduit une piste WebVTT stockée vers la langue UI du lecteur (Mistral). */
  translateSubtitlesForPlayback: (body: { trackUrl: string; sourceLanguage?: string; targetLanguage: string }) =>
    api
      .post<{ success: boolean; data: { vtt: string; targetLanguage: string } }>(
        '/video/translate-subtitles',
        body,
        { timeout: 300_000 },
      )
      .then((res) => res.data),

  /** Studio : reformule une transcription pour TTS (Mistral chat). */
  polishStudioTranscript: (text: string, language?: string) =>
    api
      .post<{ success: boolean; data: { polishedText: string } }>('/video/polish-transcript', {
        text,
        language,
      })
      .then((r) => r.data),

  /** Studio : remplace la piste audio par un fichier TTS déjà stocké (`/uploads/audio/…`). */
  muxWithAudioUrl: (videoId: string, audioUrl: string, audioOffset?: number) =>
    api
      .post<{ success: boolean; data: UserVideoAsset }>(`/video/${videoId}/mux-audio`, { audioUrl, audioOffset })
      .then((r) => r.data),
};

export default videoApi;
